import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RadarIcon from '@mui/icons-material/Radar';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import DataObjectIcon from '@mui/icons-material/DataObject';
import TerminalIcon from '@mui/icons-material/Terminal';
import KeyIcon from '@mui/icons-material/Key';
import HttpsIcon from '@mui/icons-material/Https';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import BuildIcon from '@mui/icons-material/Build';
import { useNavigate } from 'react-router-dom';
import { navigableProps } from '../utils/navigation';
import PageHeader from '../components/common/PageHeader';
import { workbenchService, WorkbenchTool } from '../services/workbenchService';

const ICON_MAP: Record<string, React.ReactElement> = {
  Radar: <RadarIcon />,
  RecordVoiceOver: <RecordVoiceOverIcon />,
  TravelExplore: <TravelExploreIcon />,
  DataObject: <DataObjectIcon />,
  Terminal: <TerminalIcon />,
  Key: <KeyIcon />,
  Https: <HttpsIcon />,
  AccountTree: <AccountTreeIcon />,
};

const CATEGORY_LABELS: Record<string, string> = {
  reconnaissance: 'Reconnaissance',
  scanning: 'Scanning',
  audio: 'Audio',
  misc: 'Sonstiges',
  diagramming: 'Diagramme',
};

const CATEGORY_COLORS: Record<string, 'primary' | 'secondary' | 'info' | 'warning' | 'success' | 'error'> = {
  reconnaissance: 'info',
  scanning: 'warning',
  audio: 'success',
  misc: 'primary',
  diagramming: 'secondary',
};

// ─── Under-the-hood info per tool ────────────────────────────

interface ToolInfoStep {
  icon: string;
  text: string;
}

interface ToolInfoEntry {
  runtime: string;
  steps: ToolInfoStep[];
}

const TOOL_INFO: Record<string, ToolInfoEntry> = {
  pta: {
    runtime: 'Backend API + externer Dienst',
    steps: [
      { icon: '1.', text: 'WAV-Datei wird an das Backend hochgeladen' },
      { icon: '2.', text: 'Backend sendet Audio an Deepgram API (nova-3 Modell)' },
      { icon: '3.', text: 'Deepgram transkribiert mit Speaker Diarization' },
      { icon: '4.', text: 'Transkript wird als Text zurückgegeben' },
      { icon: '🔑', text: 'Benötigt: DEEPGRAM_API_KEY in .env' },
      { icon: '🌐', text: 'API-Call: POST /api/pta/transcribe' },
    ],
  },
  nmap: {
    runtime: 'Docker Container (on-demand)',
    steps: [
      { icon: '1.', text: 'Target + Scan-Typ werden an Backend gesendet' },
      { icon: '2.', text: 'Backend startet Docker-Container: instrumentisto/nmap' },
      { icon: '3.', text: 'Nmap läuft isoliert im Container (max. 5 Min)' },
      { icon: '4.', text: 'stdout wird erfasst, Container wird entfernt' },
      { icon: '5.', text: 'Output wird als Text an Frontend zurückgegeben' },
      { icon: '🐳', text: 'Image: instrumentisto/nmap' },
      { icon: '🌐', text: 'API-Call: POST /api/workbench/nmap/scan' },
    ],
  },
  subfinder: {
    runtime: 'Docker Container (on-demand)',
    steps: [
      { icon: '1.', text: 'Domain wird an Backend gesendet' },
      { icon: '2.', text: 'Backend startet Docker-Container: projectdiscovery/subfinder' },
      { icon: '3.', text: 'Subfinder führt passive Enumeration durch (max. 2 Min)' },
      { icon: '4.', text: 'Gefundene Subdomains werden als sortierte Liste zurückgegeben' },
      { icon: '🐳', text: 'Image: projectdiscovery/subfinder:latest' },
      { icon: '🌐', text: 'API-Call: POST /api/workbench/subfinder/scan' },
    ],
  },
  cyberchef: {
    runtime: 'Rein lokal (Browser)',
    steps: [
      { icon: '1.', text: 'Operationen werden zur Pipeline hinzugefügt' },
      { icon: '2.', text: 'Input wird sequentiell durch alle Operationen verarbeitet' },
      { icon: '3.', text: 'Output wird live im Browser berechnet' },
      { icon: '✅', text: 'Kein Backend-Call, kein Netzwerk-Traffic' },
      { icon: '🔒', text: 'Daten verlassen niemals den Browser' },
    ],
  },
  revshell: {
    runtime: 'Rein lokal (Browser)',
    steps: [
      { icon: '1.', text: 'IP + Port werden in Shell-Templates eingesetzt' },
      { icon: '2.', text: 'Payload wird client-seitig generiert' },
      { icon: '3.', text: 'Optionales Encoding (Base64/URL) wird angewendet' },
      { icon: '✅', text: 'Kein Backend-Call, kein Netzwerk-Traffic' },
      { icon: '🔒', text: 'Daten verlassen niemals den Browser' },
    ],
  },
  jwt: {
    runtime: 'Rein lokal (Browser)',
    steps: [
      { icon: '1.', text: 'JWT wird am "." gesplittet (Header.Payload.Signature)' },
      { icon: '2.', text: 'Header + Payload werden Base64URL-dekodiert → JSON' },
      { icon: '3.', text: 'Claims werden analysiert (exp, iat, etc.)' },
      { icon: '4.', text: 'Editierter Token wird live neu zusammengebaut' },
      { icon: '✅', text: 'Kein Backend-Call, kein Netzwerk-Traffic' },
      { icon: '⚠️', text: 'Signatur kann im Browser nicht verifiziert werden' },
    ],
  },
  drawio: {
    runtime: 'Rein lokal (Browser)',
    steps: [
      { icon: '1.', text: 'diagrams.net Editor wird als iframe eingebettet' },
      { icon: '2.', text: 'Diagramme werden lokal im Browser (localStorage) gespeichert' },
      { icon: '3.', text: 'Export als PNG, SVG oder XML möglich' },
      { icon: '✅', text: 'Kein Backend-Call, kein Netzwerk-Traffic für Daten' },
      { icon: '🔒', text: 'Diagrammdaten verlassen niemals den Browser' },
    ],
  },
  'nmap-xml': {
    runtime: 'Rein lokal (Browser)',
    steps: [
      { icon: '1.', text: 'Nmap XML-Datei wird per Drag & Drop oder Texteingabe geladen' },
      { icon: '2.', text: 'DOMParser verarbeitet das XML (auch unvollständige Dateien)' },
      { icon: '3.', text: 'Hosts, Ports, Services und Scripts werden extrahiert' },
      { icon: '4.', text: 'Darstellung als strukturierte Tabelle oder GNmap-Text' },
      { icon: '✅', text: 'Kein Backend-Call, kein Netzwerk-Traffic' },
      { icon: '🔒', text: 'Daten verlassen niemals den Browser' },
    ],
  },
  testssl: {
    runtime: 'Docker Container (on-demand)',
    steps: [
      { icon: '1.', text: 'Target + Check-Typ werden an Backend gesendet' },
      { icon: '2.', text: 'Backend startet Docker-Container: drwetter/testssl.sh' },
      { icon: '3.', text: 'testssl.sh prüft TLS-Konfiguration (max. 5 Min)' },
      { icon: '4.', text: 'Analyse: Cipher Suites, Protokolle, Zertifikate, Vulns' },
      { icon: '5.', text: 'Report wird als Text an Frontend zurückgegeben' },
      { icon: '🐳', text: 'Image: drwetter/testssl.sh:3.2' },
      { icon: '🌐', text: 'API-Call: POST /api/workbench/testssl/scan' },
    ],
  },
};

const ToolInfoTooltip: React.FC<{ slug: string }> = ({ slug }) => {
  const theme = useTheme();
  const info = TOOL_INFO[slug];
  if (!info) return null;

  return (
    <Tooltip
      arrow
      enterDelay={200}
      leaveDelay={150}
      slotProps={{
        tooltip: {
          sx: {
            maxWidth: 340,
            bgcolor: theme.palette.mode === 'dark' ? '#1a1f3c' : '#fff',
            color: theme.palette.text.primary,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.shadows[8],
            p: 0,
          },
        },
        arrow: {
          sx: {
            color: theme.palette.mode === 'dark' ? '#1a1f3c' : '#fff',
            '&::before': { border: `1px solid ${theme.palette.divider}` },
          },
        },
      }}
      title={
        <Box sx={{ p: 1.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: theme.palette.primary.main, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Under the Hood
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, mb: 1 }}>
            <Chip
              label={info.runtime}
              size="small"
              variant="outlined"
              color={info.runtime.includes('lokal') ? 'success' : info.runtime.includes('Docker') ? 'info' : 'warning'}
              sx={{ fontSize: '0.65rem', height: 18 }}
            />
          </Box>
          {info.steps.map((step, idx) => (
            <Box key={idx} sx={{ display: 'flex', gap: 0.8, py: 0.35, alignItems: 'flex-start' }}>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', minWidth: 20, flexShrink: 0, fontWeight: 700, color: theme.palette.text.secondary }}>
                {step.icon}
              </Typography>
              <Typography variant="caption" sx={{ lineHeight: 1.4 }}>
                {step.text}
              </Typography>
            </Box>
          ))}
        </Box>
      }
    >
      <IconButton
        size="small"
        sx={{
          position: 'absolute',
          top: 8,
          right: 36,
          zIndex: 1,
          color: theme.palette.text.secondary,
          opacity: 0.5,
          '&:hover': { opacity: 1 },
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <InfoOutlinedIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Tooltip>
  );
};

const WorkbenchPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [tools, setTools] = useState<WorkbenchTool[]>([]);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);

  const fetchTools = useCallback(async () => {
    try {
      const data = await workbenchService.getTools();
      setTools(data);
    } catch {
      // handled by interceptor
    }
  }, []);

  useEffect(() => {
    fetchTools();
  }, [fetchTools]);

  const toggleFavorite = async (e: React.MouseEvent, tool: WorkbenchTool) => {
    e.stopPropagation();
    try {
      if (tool.is_favorite) {
        await workbenchService.removeFavorite(tool.id);
      } else {
        await workbenchService.addFavorite(tool.id);
      }
      setTools((prev) =>
        prev.map((t) =>
          t.id === tool.id ? { ...t, is_favorite: !t.is_favorite } : t,
        ),
      );
    } catch {
      // handled by interceptor
    }
  };

  const favorites = tools.filter((t) => t.is_favorite);

  const filtered = tools.filter((t) => {
    const matchesSearch =
      !search ||
      t.name.toLowerCase().includes(search.toLowerCase()) ||
      (t.description || '').toLowerCase().includes(search.toLowerCase());
    const matchesCategory = !categoryFilter || t.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const categories = Array.from(new Set(tools.map((t) => t.category)));

  const getIcon = (iconName: string | null) => {
    if (iconName && ICON_MAP[iconName]) return ICON_MAP[iconName];
    return <BuildIcon />;
  };

  const renderToolCard = (tool: WorkbenchTool, compact = false) => (
    <Grid item xs={12} sm={6} md={4} lg={3} key={tool.id}>
      <Card
        sx={{
          height: '100%',
          position: 'relative',
          transition: 'transform 0.15s, box-shadow 0.15s',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 4px 20px ${theme.palette.primary.main}22`,
          },
        }}
      >
        <ToolInfoTooltip slug={tool.slug} />
        <Tooltip title={tool.is_favorite ? 'Aus Favoriten entfernen' : 'Zu Favoriten hinzufügen'}>
          <IconButton
            size="small"
            onClick={(e) => toggleFavorite(e, tool)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 1,
              color: tool.is_favorite ? '#ffc107' : theme.palette.text.secondary,
            }}
          >
            {tool.is_favorite ? <StarIcon /> : <StarBorderIcon />}
          </IconButton>
        </Tooltip>
        <CardActionArea
          {...navigableProps(navigate, `/workbench/${tool.slug}`)}
          sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
        >
          <CardContent sx={{ flex: 1 }}>
            <Box sx={{ mb: 1 }}>
              <Box
                sx={{
                  color: theme.palette.primary.main,
                  display: 'flex',
                  alignItems: 'center',
                  '& .MuiSvgIcon-root': { fontSize: compact ? '1.5rem' : '2rem' },
                }}
              >
                {getIcon(tool.icon)}
              </Box>
            </Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5, color: theme.palette.text.primary }}>
              {tool.name}
            </Typography>
            {!compact && tool.description && (
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 1.5, lineHeight: 1.5 }}>
                {tool.description}
              </Typography>
            )}
            <Chip
              label={CATEGORY_LABELS[tool.category] || tool.category}
              size="small"
              color={CATEGORY_COLORS[tool.category] || 'primary'}
              variant="outlined"
            />
          </CardContent>
        </CardActionArea>
      </Card>
    </Grid>
  );

  return (
    <Box>
      <PageHeader title="Workbench" subtitle="Pentester-Toolbox – deine Werkzeuge an einem Ort" />

      {/* Favorites */}
      {favorites.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h6"
            sx={{ mb: 2, fontWeight: 600, color: theme.palette.text.primary }}
          >
            Favoriten
          </Typography>
          <Grid container spacing={2}>
            {favorites.map((t) => renderToolCard(t, true))}
          </Grid>
        </Box>
      )}

      {/* Catalog */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Tool suchen..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: theme.palette.text.secondary }} />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 250 }}
        />
        <ToggleButtonGroup
          value={categoryFilter}
          exclusive
          onChange={(_, v) => setCategoryFilter(v)}
          size="small"
        >
          {categories.map((cat) => (
            <ToggleButton key={cat} value={cat} sx={{ textTransform: 'none', px: 2 }}>
              {CATEGORY_LABELS[cat] || cat}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Typography
        variant="h6"
        sx={{ mb: 2, fontWeight: 600, color: theme.palette.text.primary }}
      >
        Alle Tools
      </Typography>
      <Grid container spacing={2}>
        {filtered.map((t) => renderToolCard(t))}
      </Grid>

      {filtered.length === 0 && (
        <Typography sx={{ color: theme.palette.text.secondary, mt: 2 }}>
          Keine Tools gefunden.
        </Typography>
      )}
    </Box>
  );
};

export default WorkbenchPage;
